import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useLang } from "@/context/LanguageContext";
import { CreditCard, Wallet, Truck, ChevronRight, Check, User, Zap, Gift, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import RegistrationForm from "@/components/RegistrationForm";
import GiftLines from "@/components/GiftLines";
import { saveContact } from "@/lib/saveContact";

const STEPS_KEYS = ["checkout_step_shipping", "checkout_step_payment", "checkout_step_review"];

// Supported shipping destinations — ISO-2 code + flag emoji. The visible label
// is resolved via `t(country_XX)` so it localises to IT/EN/DE. Italy stays
// pinned on top; the rest is alphabetised at render time by localised name.
const COUNTRIES = [
  { code: "IT", flag: "🇮🇹" }, { code: "AT", flag: "🇦🇹" }, { code: "BE", flag: "🇧🇪" },
  { code: "BG", flag: "🇧🇬" }, { code: "HR", flag: "🇭🇷" }, { code: "CY", flag: "🇨🇾" },
  { code: "CZ", flag: "🇨🇿" }, { code: "DK", flag: "🇩🇰" }, { code: "EE", flag: "🇪🇪" },
  { code: "FI", flag: "🇫🇮" }, { code: "FR", flag: "🇫🇷" }, { code: "DE", flag: "🇩🇪" },
  { code: "GR", flag: "🇬🇷" }, { code: "IE", flag: "🇮🇪" }, { code: "LV", flag: "🇱🇻" },
  { code: "LT", flag: "🇱🇹" }, { code: "LU", flag: "🇱🇺" }, { code: "MT", flag: "🇲🇹" },
  { code: "NL", flag: "🇳🇱" }, { code: "PL", flag: "🇵🇱" }, { code: "PT", flag: "🇵🇹" },
  { code: "RO", flag: "🇷🇴" }, { code: "SK", flag: "🇸🇰" }, { code: "SI", flag: "🇸🇮" },
  { code: "ES", flag: "🇪🇸" }, { code: "SE", flag: "🇸🇪" }, { code: "HU", flag: "🇭🇺" },
  { code: "GB", flag: "🇬🇧" }, { code: "CH", flag: "🇨🇭" }, { code: "SM", flag: "🇸🇲" },
];

// --- Meta attribution helpers ------------------------------------------------
// Read a cookie value by name (safe on SSR / disabled cookies).
const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[-.]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
};

// Build the attribution payload — merges Meta pixel cookies (`_fbc`,`_fbp`),
// the raw `fbclid` (surviving after redirects via sessionStorage), UTMs, and a
// fresh event_id for CAPI+client-pixel deduplication.
const buildAttribution = () => {
  const attr = { user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null };
  attr.fbc = readCookie("_fbc") || null;
  attr.fbp = readCookie("_fbp") || null;
  // GA4 client_id (from `_ga` cookie captured by GoogleAnalytics.jsx). Sent to the
  // backend so the Purchase Measurement Protocol event stitches to the visitor.
  try { attr.ga_client_id = sessionStorage.getItem("audrivio_ga_client_id") || null; } catch { attr.ga_client_id = null; }
  try {
    const params = new URLSearchParams(window.location.search);
    attr.fbclid = params.get("fbclid") || sessionStorage.getItem("audrivio_fbclid") || null;
    attr.ttclid = params.get("ttclid") || sessionStorage.getItem("audrivio_ttclid") || null;
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      attr[k] = params.get(k) || sessionStorage.getItem(`audrivio_${k}`) || null;
    });
  } catch { /* storage disabled */ }
  const eid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID()
    : "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
  attr.event_id = eid;
  return Object.fromEntries(Object.entries(attr).filter(([, v]) => v));
};

export default function Checkout() {
  const { items, subtotal, clear, promo, applyPromo, removePromo, gifts, replaceItems } = useCart();
  const [searchParams] = useSearchParams();
  const productsParam = searchParams.get("products");
  const [loadingLink, setLoadingLink] = useState(() => {
    try { return !!productsParam && sessionStorage.getItem("audrivio_products_link") !== productsParam; } catch { return !!productsParam; }
  });
  const { customer } = useCustomerAuth();
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [shipping, setShipping] = useState({ full_name: "", email: "", phone: "", address_line: "", civic_number: "", city: "", postal_code: "", country: "IT" });
  const [payment, setPayment] = useState("card");
  const [couponCode, setCouponCode] = useState("");
  const coupon = promo && promo.kind !== "gift" ? promo : null;
  const [processing, setProcessing] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const nav = useNavigate();

  // Ads checkout link: /checkout?products=ID:QTY,ID2:QTY&coupon=CODE → replaces cart once per link.
  useEffect(() => {
    if (!loadingLink) return;
    const pairs = productsParam.split(",").map((x) => x.trim()).filter(Boolean).map((x) => {
      const [id, q] = decodeURIComponent(x).split(":");
      return { id: id.trim(), qty: Math.max(1, parseInt(q, 10) || 1) };
    });
    Promise.all(pairs.map((p) => api.get(`/products/${encodeURIComponent(p.id)}`).then((r) => ({ product: r.data, qty: p.qty })).catch(() => null)))
      .then((res) => {
        const lines = res.filter((r) => r && !r.product.is_gift);
        if (lines.length) replaceItems(lines);
        else toast.error(t("checkout_link_invalid"));
        try { sessionStorage.setItem("audrivio_products_link", productsParam); } catch { /* ignore */ }
      })
      .finally(() => setLoadingLink(false));
  }, [loadingLink]); // eslint-disable-line

  // Re-validate a persisted promo (admin may have disabled it or uses may be exhausted).
  useEffect(() => {
    if (promo?.code) applyPromo(promo.code).catch(() => { removePromo(); toast.error(t("checkout_coupon_invalid")); });
  }, []); // eslint-disable-line

  // Save contact as soon as email+phone are typed so an abandoned checkout stays recoverable.
  useEffect(() => {
    const email = shipping.email.trim().toLowerCase();
    const phone = shipping.phone.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || phone.replace(/\D/g, "").length < 6) return;
    const key = `${email}|${phone}`;
    const h = setTimeout(() => {
      try { if (sessionStorage.getItem("audrivio_identified") === key) return; sessionStorage.setItem("audrivio_identified", key); } catch { /* ignore */ }
      saveContact({ email, phone, name: shipping.full_name.trim() || null, items, subtotal, source: "checkout" }).catch(() => {});
    }, 1500);
    return () => clearTimeout(h);
  }, [shipping.email, shipping.phone]); // eslint-disable-line

  // Prefill from logged-in customer
  useEffect(() => {
    if (!customer) return;
    const a = customer.address || {};
    setShipping((cur) => ({
      full_name: cur.full_name || customer.name || "",
      email: cur.email || customer.email || "",
      phone: cur.phone || customer.phone || "",
      address_line: cur.address_line || a.address_line || "",
      civic_number: cur.civic_number || a.civic_number || "",
      city: cur.city || a.city || "",
      postal_code: cur.postal_code || a.postal_code || "",
      country: cur.country || a.country || "IT",
    }));
  }, [customer]);

  // Meta Pixel: InitiateCheckout — fires when the user REACHES /checkout, not on
  // final submit. Previously the event was buried inside handleSubmit → Meta only
  // registered it for users who completed the full 8-field shipping form + picked
  // a payment method + clicked "Effettua ordine". This under-counted the funnel
  // step massively (Ads Manager showed 6.3% AddToCart→IC; real ratio is closer to
  // 30-40%). Now IC fires at arrival, matching how Meta expects funnel steps to
  // be reported. Guarded with a sessionStorage flag to fire at most once per session.
  useEffect(() => {
    if (!items || items.length === 0 || loadingLink) return;
    let alreadyFired = false;
    try { alreadyFired = sessionStorage.getItem("audrivio_ic_fired") === "1"; } catch { /* ignore */ }
    if (alreadyFired) return;
    if (typeof window === "undefined" || typeof window.audrivioTrack !== "function") return;
    window.audrivioTrack("InitiateCheckout", {
      content_ids: items.map((i) => i.product_id),
      num_items: items.reduce((s, i) => s + i.quantity, 0),
      currency: "EUR",
      value: subtotal,
    });
    try { sessionStorage.setItem("audrivio_ic_fired", "1"); } catch { /* ignore */ }
  }, [items, subtotal, loadingLink]);

  const COD_FEE = 10; // Contrassegno: +€10.00 automatici lato server e lato UI
  const FOREIGN_SHIPPING_FEE = 20; // Spedizione internazionale flat, allineato al backend
  // Auto-switch off COD if user selects a foreign shipping country: COD only for Italy.
  const isForeign = (shipping.country || "IT").trim().toUpperCase() !== "IT";
  useEffect(() => {
    if (isForeign && payment === "cod") setPayment("card");
  }, [isForeign, payment]);
  const codFee = (!isForeign && payment === "cod") ? COD_FEE : 0;
  const shippingFee = isForeign ? FOREIGN_SHIPPING_FEE : 0;
  const total = useMemo(() => {
    let t = subtotal;
    if (coupon) {
      t = coupon.kind === "percent" ? Math.max(0, subtotal - subtotal * (coupon.value / 100)) : Math.max(0, subtotal - coupon.value);
    }
    return t + codFee + shippingFee;
  }, [subtotal, coupon, codFee, shippingFee]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    try {
      const p = await applyPromo(couponCode);
      setCouponCode("");
      toast.success(`${p.code}: ${t(p.kind === "gift" ? "promo_gift_applied" : "checkout_coupon_applied")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("checkout_coupon_invalid"));
    }
  };

  const submit = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem("xd_customer_token");
      // Build a single shared attribution object with a fresh event_id so we can
      // propagate the same identifiers to the backend, which will persist them on
      // the order and pass them to the CAPI Purchase event on payment settle.
      // NOTE: InitiateCheckout fires from the mount useEffect above (once per
      // session), not here — this handler is the Purchase intent path.
      const attribution = buildAttribution();
      const purchaseEventId = attribution.event_id;
      // Persist the SHARED purchase event_id so /checkout/success can fire the client
      // Pixel Purchase with the same id → Meta deduplicates client + CAPI events.
      try { sessionStorage.setItem("audrivio_purchase_event_id", purchaseEventId); } catch { /* ignore */ }
      const res = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, variant_id: i.variant_id || null, variant_title: i.variant_title || null })),
        shipping,
        payment_method: payment,
        coupon_code: promo?.code || null,
        origin_url: window.location.origin,
        attribution,
        lang,
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        clear();
        nav(`/checkout/success?order=${res.data.order.order_number}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("checkout_coupon_error_toast"));
    } finally {
      setProcessing(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return Object.values(shipping).every((v) => v && v.trim());
    return true;
  };

  const promoCountryBlocked = !!promo?.valid_countries?.length && !promo.valid_countries.includes((shipping.country || "IT").toUpperCase());

  if (loadingLink) {
    return (
      <div className="min-h-screen bg-xd-light">
        <Header />
        <div className="max-w-3xl mx-auto text-center py-20 text-xd-muted" data-testid="checkout-link-loading">{t("checkout_link_loading")}</div>
        <Footer />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-xd-light">
        <Header />
        <div className="max-w-3xl mx-auto text-center py-20">
          <div className="text-5xl mb-4">🛒</div>
          <div className="font-display text-2xl font-semibold">{t("cart_empty")}</div>
          <p className="text-xd-muted mt-2">{t("checkout_empty_cart_desc")}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-xd-light">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-semibold mb-6">{t("checkout_title")}</h1>

        {/* Stepper */}
        <ol className="flex items-center gap-2 mb-8 text-sm">
          {STEPS_KEYS.map((k, i) => (
            <li key={k} className="flex items-center gap-2">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${i <= step ? "bg-xd-cyan border-xd-cyan text-xd-dark" : "border-xd-border text-xd-muted"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</span>
              <span className={i === step ? "font-semibold" : "text-xd-muted"}>{t(k)}</span>
              {i < STEPS_KEYS.length - 1 && <ChevronRight className="w-4 h-4 text-xd-muted" />}
            </li>
          ))}
        </ol>

        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="bg-white rounded-xl border border-xd-border p-6">
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="font-display text-lg font-semibold">{t("checkout_shipping_title")}</h2>

                {/* Optional guest → account creation banner */}
                {!customer && (
                  <div className="border border-xd-cyan/40 bg-xd-cyan/5 rounded-lg p-4" data-testid="checkout-register-block">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 font-display font-semibold text-sm"><User className="w-4 h-4 text-xd-cyanHover" /> {t("checkout_register_title")}</div>
                        <p className="text-xs text-xd-muted mt-1">{t("checkout_register_desc")}</p>
                      </div>
                      <button type="button" onClick={() => setShowRegister((v) => !v)} data-testid="checkout-register-toggle" className={`shrink-0 h-9 px-3 rounded-md text-xs font-semibold transition-colors ${showRegister ? "bg-white border border-xd-border" : "bg-xd-cyan text-xd-dark"}`}>
                        {showRegister ? t("checkout_register_guest") : t("checkout_register_cta")}
                      </button>
                    </div>
                    {showRegister && (
                      <div className="mt-4 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-4 border-t border-xd-border" data-testid="checkout-register-form-wrap">
                        <RegistrationForm
                          role="customer"
                          embedded
                          onSuccess={() => {
                            toast.success(t("checkout_register_success"));
                            setShowRegister(false);
                          }}
                        />
                        <p className="text-[11px] text-xd-muted mt-2">{t("checkout_partner_note")} <Link to="/fornitori/registrati" className="underline">{t("checkout_partner_link")}</Link> {t("checkout_partner_suffix")}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3">
                  <Input required placeholder={t("checkout_field_fullname")} value={shipping.full_name} onChange={(e) => setShipping({ ...shipping, full_name: e.target.value })} data-testid="ship-fullname" />
                  <Input required type="email" placeholder={t("checkout_field_email")} value={shipping.email} onChange={(e) => setShipping({ ...shipping, email: e.target.value })} data-testid="ship-email" />
                  <div>
                    <Input required placeholder={t("checkout_field_phone")} value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} data-testid="ship-phone" />
                    <p className="mt-1 text-[11px] text-xd-muted" data-testid="ship-phone-note">{t("checkout_phone_note")}</p>
                  </div>
                  <Input required placeholder={t("checkout_field_city")} value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} data-testid="ship-city" />
                  <div className="sm:col-span-2 grid grid-cols-[1fr_120px] gap-3">
                    <Input required placeholder={t("checkout_field_address")} value={shipping.address_line} onChange={(e) => setShipping({ ...shipping, address_line: e.target.value })} data-testid="ship-address" />
                    <Input required placeholder={t("checkout_field_civic")} value={shipping.civic_number} onChange={(e) => setShipping({ ...shipping, civic_number: e.target.value })} data-testid="ship-civic" />
                  </div>
                  <Input required placeholder={t("checkout_field_postal")} value={shipping.postal_code} onChange={(e) => setShipping({ ...shipping, postal_code: e.target.value })} data-testid="ship-postal" />
                  <select
                    required
                    value={shipping.country || "IT"}
                    onChange={(e) => setShipping({ ...shipping, country: e.target.value })}
                    data-testid="ship-country"
                    aria-label={t("checkout_field_country")}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    {(() => {
                      // Italy pinned on top, then the rest alphabetised by the
                      // localised country name (depends on current `lang`).
                      const it = COUNTRIES.find((c) => c.code === "IT");
                      const rest = COUNTRIES
                        .filter((c) => c.code !== "IT")
                        .sort((a, b) => t(`country_${a.code}`).localeCompare(t(`country_${b.code}`), lang));
                      return [it, ...rest].map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {t(`country_${c.code}`)}</option>
                      ));
                    })()}
                  </select>
                </div>
                <p className="text-[11px] text-xd-muted">{t("checkout_shipping_note")}</p>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <h2 className="font-display text-lg font-semibold">{t("checkout_payment_title")}</h2>
                <RadioGroup value={payment} onValueChange={setPayment} className="grid gap-3">
                  {[
                    { v: "card", i: CreditCard, l: t("checkout_pay_card"), d: t("checkout_pay_card_desc") },
                    // Klarna always available — no minimum threshold on our side. Klarna itself
                    // will decide approval based on their own risk rules at checkout time.
                    { v: "klarna", i: Zap, l: t("checkout_pay_klarna"), d: `${t("checkout_pay_klarna_installment")} € ${(total / 3).toFixed(2)} ${t("checkout_pay_klarna_approval")}` },
                    { v: "paypal", i: Wallet, l: t("checkout_pay_paypal"), d: t("checkout_pay_paypal_long") },
                    // Cash on delivery is Italy-only (courier constraint). We hide it entirely
                    // for foreign shipping addresses instead of showing it disabled — cleaner UX.
                    ...(isForeign ? [] : [{ v: "cod", i: Truck, l: t("checkout_pay_cod"), d: t("checkout_pay_cod_desc") }]),
                  ].map((m) => (
                    <label key={m.v} className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${payment === m.v ? "border-xd-cyan bg-xd-cyan/5" : "border-xd-border hover:border-xd-cyan/40"}`}>
                      <RadioGroupItem value={m.v} id={`pay-${m.v}`} data-testid={`pay-${m.v}`} />
                      <m.i className="w-5 h-5 text-xd-text mt-0.5" />
                      <div>
                        <div className="font-medium text-sm">{m.l}</div>
                        <div className="text-xs text-xd-muted">{m.d}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <h2 className="font-display text-lg font-semibold">{t("checkout_review_title")}</h2>
                <div className="rounded-lg border border-xd-border divide-y divide-xd-border">
                  {items.map((i) => (
                    <div key={i.cart_key} className="flex items-center gap-3 p-3">
                      <div className="w-12 h-12 rounded bg-slate-100 overflow-hidden">{i.image && <img src={i.image} alt="" className="w-full h-full object-cover" />}</div>
                      <div className="flex-1 text-sm">
                        <div className="font-medium leading-tight">{i.name}</div>
                        <div className="text-xs text-xd-muted">{t("checkout_qty")} {i.quantity}</div>
                      </div>
                      <div className="text-sm font-semibold">€ {(i.price * i.quantity).toFixed(2)}</div>
                    </div>
                  ))}
                </div>
                <GiftLines />
                <div className="text-xs text-xd-muted">{t("checkout_ship_to")}: <strong>{shipping.full_name}</strong>, {shipping.address_line} {shipping.civic_number}, {shipping.city} {shipping.postal_code}, {shipping.country}</div>
                <div className="text-xs text-xd-muted">{t("checkout_payment_label")} <strong>{payment}</strong></div>
              </div>
            )}

            <div className="mt-6 flex justify-between gap-3">
              {step > 0 ? (
                <button type="button" onClick={() => setStep(step - 1)} data-testid="checkout-back" className="text-sm text-xd-muted hover:text-xd-text">{t("checkout_back_arrow")}</button>
              ) : <span />}
              {step < 2 ? (
                <button type="button" disabled={!canProceed()} onClick={() => setStep(step + 1)} data-testid="checkout-next" className="btn-cta rounded-lg px-6 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed">{t("checkout_next")}</button>
              ) : (
                <button type="button" disabled={processing} onClick={submit} data-testid="checkout-place-order" className="btn-cta rounded-lg px-6 py-3 text-sm disabled:opacity-50">
                  {processing ? t("checkout_submitting") : t("checkout_confirm_order")}
                </button>
              )}
            </div>
          </div>

          <aside className="bg-white rounded-xl border border-xd-border p-6 h-fit sticky top-24">
            <div className="font-display text-lg font-semibold mb-4">{t("checkout_step_review")}</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-xd-muted">{t("cart_subtotal")}</span><span data-testid="summary-subtotal">€ {subtotal.toFixed(2)}</span></div>
              {gifts.length > 0 && (
                <div className="flex justify-between text-emerald-700" data-testid="summary-gifts"><span className="inline-flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> {t("promo_gift_summary")} ({gifts.length})</span><span>€ 0.00</span></div>
              )}
              {coupon && <div className="flex justify-between text-xd-success"><span>{t("checkout_summary_discount")} ({coupon.code})</span><span>- € {(coupon.kind === 'percent' ? subtotal * (coupon.value / 100) : coupon.value).toFixed(2)}</span></div>}
              <div className="flex justify-between" data-testid="summary-shipping">
                <span className="text-xd-muted">{t("checkout_summary_shipping")}</span>
                {isForeign ? (
                  <span>+ € {shippingFee.toFixed(2)}</span>
                ) : (
                  <span className="text-xd-success">{t("checkout_summary_shipping_free")}</span>
                )}
              </div>
              <div className="text-[11px] text-xd-muted -mt-1" data-testid="summary-shipping-eta">
                {isForeign ? t("checkout_eta_foreign") : t("checkout_eta_it")}
              </div>
              {codFee > 0 && (
                <div className="flex justify-between" data-testid="summary-cod-fee"><span className="text-xd-muted">{t("checkout_summary_cod")}</span><span>+ € {codFee.toFixed(2)}</span></div>
              )}
              <div className="border-t border-xd-border pt-2 flex justify-between font-display text-lg font-semibold"><span>{t("checkout_total")}</span><span data-testid="summary-total">€ {total.toFixed(2)}</span></div>
            </div>
            {promo && (
              <div className="mt-4 space-y-2">
                <div data-testid="promo-applied" className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
                  <span className="font-mono-tech font-semibold text-emerald-700">{promo.code}</span>
                  <button type="button" onClick={removePromo} data-testid="promo-remove" className="inline-flex items-center gap-1 text-xd-muted hover:text-red-500"><X className="w-3 h-3" /> {t("cart_remove")}</button>
                </div>
                {promoCountryBlocked && <div data-testid="promo-country-warning" className="text-[11px] text-red-600">{t("promo_country_blocked")}</div>}
                <GiftLines compact />
              </div>
            )}
            <div className="mt-4">
              <Label className="text-xs uppercase tracking-widest font-mono-tech text-xd-muted">{t("checkout_coupon_label")}</Label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="WELCOME10" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} data-testid="coupon-input" />
                <button type="button" onClick={applyCoupon} data-testid="coupon-apply" className="btn-cyan px-4 rounded-md text-sm">{t("checkout_coupon_apply_short")}</button>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
